<template>
  <div class="h-full flex flex-col rounded-xl bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md ring-1 ring-indigo-300/40 dark:ring-indigo-500/30">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.chat.title') }}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ resolvedSubtitle }}</p>
        </div>
      </div>
      <span
        v-if="planBlocked"
        class="task-chat-plan-lock"
        :title="t('kanban.chat.planLock')"
        :aria-label="t('kanban.chat.planLock')"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </span>
      <button
        v-if="!planBlocked"
        @click="loadMessages"
        class="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:hover:border-blue-500/40 transition-all"
        :title="t('kanban.chat.refresh')"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>

    <PlanFeatureGate
      :blocked="planBlocked"
      :title="planGateTitle"
      :description="planGateDescription"
      class="task-chat-plan-gate flex-1 min-h-0 flex flex-col h-full"
    >
    <div class="task-chat-plan-body flex flex-col flex-1 min-h-0 h-full overflow-hidden">
    <div ref="messagesContainer" class="task-chat-messages flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
      <div
        v-if="endpointUnavailable"
        class="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
      >
        {{ t('kanban.chat.unavailable') }}
      </div>
      <div v-if="loading" class="text-sm text-gray-500 dark:text-gray-400 py-3 px-2">{{ t('kanban.chat.loading') }}</div>
      <div v-else-if="messages.length === 0" class="text-sm text-gray-500 dark:text-gray-400 py-8 px-2 text-center">
        {{ resolvedEmptyState }}
      </div>

      <div
        v-for="message in messages"
        :key="message.id"
        class="w-full"
      >
        <div
          class="flex items-end gap-2"
          :class="isOwnMessage(message) ? 'justify-end' : 'justify-start'"
        >
          <div
            v-if="!isOwnMessage(message)"
            class="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs flex items-center justify-center font-semibold shadow-sm shrink-0"
          >
            <img
              v-if="message.usuarioFotoPerfil"
              :src="message.usuarioFotoPerfil"
              :alt="message.usuarioNome"
              class="w-full h-full object-cover"
            />
            <span v-else>{{ getInitials(message.usuarioNome) }}</span>
          </div>

          <div
            class="task-chat-bubble max-w-[min(92%,380px)] rounded-xl overflow-hidden border shadow-sm"
            :class="isOwnMessage(message)
              ? 'task-chat-bubble--out border-gray-200 dark:border-gray-600'
              : 'task-chat-bubble--in border-gray-200 dark:border-gray-600'"
          >
            <div class="flex items-center gap-2 px-3 pt-2 pb-1 min-w-0">
              <span
                class="text-[11px] font-medium truncate flex-1 min-w-0"
                :class="isOwnMessage(message)
                  ? 'text-gray-800'
                  : (incomingBubbleLightText ? 'text-gray-700' : 'text-gray-700 dark:text-gray-200')"
              >{{ message.usuarioNome || t('kanban.chat.defaultUser') }}</span>
              <span class="text-[10px] text-gray-500 dark:text-gray-400 shrink-0">{{ formatDateTime(message.dataCriacao) }}</span>
            </div>

            <WhatsAppYoutubePreview
              v-for="yt in getYoutubePreviews(message.mensagem)"
              :key="`yt-${message.id}-${yt.videoId}`"
              :video-id="yt.videoId"
              :url="yt.url"
            />

            <div
              v-if="messageTextForDisplay(message.mensagem)"
              class="task-chat-bubble__text whatsapp-message-body px-3 pb-1 text-sm"
              :class="isOwnMessage(message)
                ? 'text-gray-800'
                : (incomingBubbleLightText ? 'text-gray-800' : 'text-gray-800 dark:text-gray-100')"
              v-html="renderMessageHtml(messageTextForDisplay(message.mensagem))"
            />

            <div
              v-for="attachment in imageAttachments(message)"
              :key="`img-${attachment.id || attachment.urlAcesso || attachment.nomeOriginal}`"
              class="task-chat-bubble__media px-1 pb-1"
            >
              <button
                type="button"
                class="block w-full rounded-lg overflow-hidden border-0 p-0 bg-transparent cursor-zoom-in"
                @click="openImagePreview(attachment)"
              >
                <img
                  :src="resolveAttachmentUrl(attachment)"
                  :alt="attachment.nomeOriginal || t('kanban.chat.imageAlt')"
                  class="wa-media__img"
                  loading="lazy"
                />
              </button>
            </div>

            <div
              v-for="attachment in fileAttachments(message)"
              :key="`file-${attachment.id || attachment.urlAcesso || attachment.nomeOriginal}`"
              class="px-2 pb-2"
            >
              <a
                :href="resolveAttachmentUrl(attachment)"
                target="_blank"
                rel="noopener noreferrer"
                class="wa-doc-card"
                :class="isOwnMessage(message) ? 'wa-doc-card--out' : 'wa-doc-card--in'"
              >
                <div class="wa-doc-card__icon" :class="attachmentFileIconClass(attachment)" aria-hidden="true">
                  <span class="wa-doc-card__ext">{{ attachmentFileExtension(attachment) }}</span>
                </div>
                <div class="wa-doc-card__body min-w-0">
                  <p class="wa-doc-card__name">{{ attachmentDisplayName(attachment, t) }}</p>
                  <p class="wa-doc-card__meta">
                    {{ attachmentFileTypeLabel(attachmentFileExtension(attachment), t) }}
                    <template v-if="formatSize(attachment.tamanho)"> · {{ formatSize(attachment.tamanho) }}</template>
                    · {{ t('kanban.chat.tapToOpen') }}
                  </p>
                </div>
                <div class="wa-doc-card__action" aria-hidden="true">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                  </svg>
                </div>
              </a>
            </div>
          </div>

          <div
            v-if="isOwnMessage(message)"
            class="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs flex items-center justify-center font-semibold shadow-sm shrink-0"
          >
            <img
              v-if="message.usuarioFotoPerfil"
              :src="message.usuarioFotoPerfil"
              :alt="message.usuarioNome"
              class="w-full h-full object-cover"
            />
            <span v-else>{{ getInitials(message.usuarioNome) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="task-chat-composer shrink-0 p-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
      <div v-if="pendingAttachments.length > 0" class="space-y-1.5">
        <p class="text-[11px] font-medium text-gray-500 dark:text-gray-400 px-1">{{ t('kanban.chat.pendingAttachments') }}</p>
        <div
          v-for="attachment in pendingAttachments"
          :key="attachment.id || attachment.urlAcesso || attachment.nomeOriginal"
          class="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/60 px-2.5 py-2"
        >
          <div class="flex items-center space-x-2 min-w-0">
            <span class="text-sm">{{ attachment.ehImagem ? '🖼️' : '📎' }}</span>
            <span class="text-xs text-gray-700 dark:text-gray-200 truncate">{{ attachment.nomeOriginal }}</span>
          </div>
          <button
            @click="removePendingAttachment(attachment)"
            class="text-xs font-medium text-red-500 hover:text-red-600"
            :title="t('kanban.chat.removeAttachment')"
          >
            {{ t('kanban.chat.remove') }}
          </button>
        </div>
      </div>

      <div class="rounded-xl bg-gray-50 dark:bg-gray-700/30 p-2.5">
        <div class="relative">
          <textarea
            ref="messageInputRef"
            v-model="newMessage"
            data-chat-input="true"
            rows="2"
            :placeholder="t('kanban.chat.placeholder')"
            class="w-full min-w-0 px-3 py-2 pr-24 text-sm border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 dark:bg-gray-800 dark:text-white"
            @keydown.enter.exact.prevent="sendMessage"
            @paste="handlePasteInChat"
          ></textarea>

          <input
            ref="fileInput"
            type="file"
            class="hidden"
            multiple
            accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
            @change="handleFileSelect"
          />

          <div class="absolute right-2 bottom-2 flex items-center gap-1.5">
            <button
              @click="openFileDialog"
              :disabled="uploading || endpointUnavailable"
              class="h-8 w-8 inline-flex items-center justify-center rounded-md border border-transparent text-gray-500 dark:text-gray-300 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700/70 hover:border-gray-200 dark:hover:border-gray-600 disabled:opacity-50 transition-colors"
              :title="t('kanban.chat.attach')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
              </svg>
            </button>

            <button
              @click="sendMessage"
              :disabled="endpointUnavailable || sending || uploading || (!newMessage.trim() && pendingAttachments.length === 0)"
              class="h-8 w-8 inline-flex items-center justify-center rounded-md border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/25 hover:bg-blue-100 dark:hover:bg-blue-900/35 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :title="t('kanban.chat.send')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-9.193-5.106A1 1 0 004 6.94v10.12a1 1 0 001.559.832l9.193-5.106a1 1 0 000-1.664z"></path>
              </svg>
            </button>
          </div>
        </div>
        <div class="mt-1.5 px-1 flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
          <span>{{ t('kanban.chat.enterToSend') }}</span>
          <span v-if="uploading">{{ t('kanban.chat.uploadingAttachment') }}</span>
          <span v-else-if="pendingAttachments.length > 0">{{ t('kanban.chat.attachmentsCount', { count: pendingAttachments.length }) }}</span>
          <span v-else>&nbsp;</span>
        </div>
      </div>
    </div>
    </div>
    </PlanFeatureGate>

    <div
      v-if="previewImage"
      class="fixed inset-0 z-[13050] bg-black/70 flex items-center justify-center p-4"
      data-image-preview-overlay
      role="dialog"
      aria-modal="true"
      :aria-label="t('kanban.chat.previewAria')"
      tabindex="-1"
      @click="closeImagePreview"
      @keydown.escape.stop="closeImagePreview"
    >
      <button
        type="button"
        class="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/75"
        :aria-label="t('common.close')"
        data-image-preview-close
        @click.stop="closeImagePreview"
      >
        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div class="max-w-5xl max-h-[90vh] w-full" @click.stop>
        <img
          :src="resolveAttachmentUrl(previewImage)"
          :alt="previewImage.nomeOriginal || t('kanban.chat.imageAlt')"
          class="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg shadow-2xl"
        />
        <div class="mt-2 text-center text-xs text-white/90">
          {{ previewImage.nomeOriginal || t('kanban.chat.imageAlt') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import { resolveMediaUrl } from '@/config/app'
import PlanFeatureGate from './plan/PlanFeatureGate.vue'
import WhatsAppYoutubePreview from './WhatsAppYoutubePreview.vue'
import { parseYoutubeLinksFromText, textWithoutYoutubeUrls } from '../utils/youtubeLink'
import { formatWhatsAppMessageHtml, normalizeMessageLineBreaks } from '../utils/whatsappMessageFormat'
import { pushEscapeLayer } from '@/utils/escapeLayerStack'
import {
  attachmentDisplayName,
  attachmentFileExtension,
  attachmentFileIconClass,
  attachmentFileTypeLabel
} from '../utils/chatAttachmentDisplay'

const { t, locale } = useI18n()
const emit = defineEmits(['chatAnexosUpdated'])

const props = defineProps({
  taskId: {
    type: Number,
    required: false,
    default: null
  },
  leadId: {
    type: Number,
    required: false,
    default: null
  },
  clienteId: {
    type: Number,
    required: false,
    default: null
  },
  subtitle: {
    type: String,
    default: ''
  },
  emptyStateText: {
    type: String,
    default: ''
  },
  planBlocked: {
    type: Boolean,
    default: false
  },
  planGateTitle: {
    type: String,
    default: 'Histórico (chat) bloqueado no plano atual'
  },
  planGateDescription: {
    type: String,
    default: 'O plano do dono deste workspace não inclui esta opção.'
  },
  /** Bolhas recebidas: texto escuro também no tema escuro (ex.: modal de task no Kanban) */
  incomingBubbleLightText: {
    type: Boolean,
    default: false
  }
})

const isLeadMode = computed(() => props.leadId != null && Number(props.leadId) > 0)
const isClienteMode = computed(() => props.clienteId != null && Number(props.clienteId) > 0)

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const resolvedSubtitle = computed(() => {
  if (props.subtitle) return props.subtitle
  if (isLeadMode.value) return t('kanban.chat.subtitleLead')
  if (isClienteMode.value) return t('workspaceClienteModal.chat.subtitle')
  return t('kanban.chat.subtitleTask')
})

const resolvedEmptyState = computed(() => {
  if (props.emptyStateText) return props.emptyStateText
  if (isLeadMode.value) return t('kanban.chat.emptyLead')
  if (isClienteMode.value) return t('workspaceClienteModal.chat.empty')
  return t('kanban.chat.emptyTask')
})

const chatMessagesPath = computed(() => {
  if (isLeadMode.value) return `/leads/${props.leadId}/chat/messages`
  if (isClienteMode.value) return `/clientes/${props.clienteId}/chat/messages`
  if (props.taskId) return `/tasks/${props.taskId}/chat/messages`
  return null
})

const enrichMessage = (message) => {
  if (!message) return message
  const foto = message.usuarioFotoPerfil
  return {
    ...message,
    usuarioFotoPerfil: foto ? (resolveMediaUrl(foto) || foto) : null
  }
}

const messages = ref([])
const loading = ref(false)
const sending = ref(false)
const uploading = ref(false)
const endpointUnavailable = ref(false)
const newMessage = ref('')
const pendingAttachments = ref([])
const fileInput = ref(null)
const messagesContainer = ref(null)
const messageInputRef = ref(null)
const previewImage = ref(null)
const currentUserId = ref(null)

const scrollToBottom = async () => {
  await nextTick()
  const el = messagesContainer.value
  if (el) {
    el.scrollTop = el.scrollHeight
  }
}

const safeJsonParse = (value, fallback = []) => {
  try {
    if (value == null || value === '') return fallback
    if (Array.isArray(value)) return value
    return JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

const parseAttachments = (attachments) => safeJsonParse(attachments, [])

const isImageAttachment = (attachment) => {
  if (!attachment) return false
  if (attachment.ehImagem === true) return true
  if (typeof attachment.ehImagem === 'string' && attachment.ehImagem.toLowerCase() === 'true') return true
  const mime = (attachment.tipoArquivo || '').toLowerCase()
  return mime.startsWith('image/')
}

const getInitials = (name) => {
  const value = (name || '').trim()
  if (!value) return 'U'
  const parts = value.split(/\s+/).slice(0, 2)
  return parts.map(p => p.charAt(0).toUpperCase()).join('')
}

const parseBackendDate = (value) => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  // Epoch timestamp (ms/s) numérico
  if (typeof value === 'number') {
    const ts = value > 1e12 ? value : value * 1000
    const fromTs = new Date(ts)
    return Number.isNaN(fromTs.getTime()) ? null : fromTs
  }

  // Formato array comum do Jackson para LocalDateTime:
  // [yyyy, MM, dd, HH, mm, ss, nanos]
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nanos = 0] = value
    if (!year || !month || !day) return null
    const ms = Math.floor(Number(nanos || 0) / 1000000)
    const fromArray = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      ms
    )
    return Number.isNaN(fromArray.getTime()) ? null : fromArray
  }

  // Objeto com chaves de data/hora (fallback defensivo)
  if (typeof value === 'object') {
    const y = Number(value.year ?? value.ano)
    const m = Number(value.monthValue ?? value.month ?? value.mes)
    const d = Number(value.dayOfMonth ?? value.day ?? value.dia)
    const h = Number(value.hour ?? value.hora ?? 0)
    const mi = Number(value.minute ?? value.minuto ?? 0)
    const s = Number(value.second ?? value.segundo ?? 0)
    const nano = Number(value.nano ?? value.nanosecond ?? 0)
    if (y && m && d) {
      const ms = Math.floor(nano / 1000000)
      const fromObj = new Date(y, m - 1, d, h, mi, s, ms)
      return Number.isNaN(fromObj.getTime()) ? null : fromObj
    }
  }

  const raw = String(value).trim()
  if (!raw) return null

  // Epoch timestamp em string
  if (/^\d{10,13}$/.test(raw)) {
    const num = Number(raw)
    const ts = raw.length >= 13 ? num : num * 1000
    const fromRawTs = new Date(ts)
    if (!Number.isNaN(fromRawTs.getTime())) return fromRawTs
  }

  // Tenta parser nativo primeiro
  let d = new Date(raw)
  if (!Number.isNaN(d.getTime())) return d

  // Suporta formatos de LocalDateTime do backend Java:
  // yyyy-MM-dd HH:mm:ss(.SSS...) e yyyy-MM-ddTHH:mm:ss(.SSS...)
  const normalized = raw.replace(' ', 'T')
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/
  )

  if (!match) return null

  const [, year, month, day, hour, minute, second = '0', fraction = '0'] = match
  const ms = Number((fraction + '000').slice(0, 3))
  d = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    ms
  )

  return Number.isNaN(d.getTime()) ? null : d
}

const formatDate = (value) => {
  const d = parseBackendDate(value)
  if (!d) return ''
  return d.toLocaleString(dateLocale.value)
}

const formatDateTime = (value) => {
  const d = parseBackendDate(value)
  if (!d) return ''
  return d.toLocaleString(dateLocale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const isOwnMessage = (message) => {
  if (!message || currentUserId.value == null) return false
  return Number(message.usuarioId) === Number(currentUserId.value)
}

const formatSize = (bytes) => {
  const size = Number(bytes || 0)
  if (size <= 0) return ''
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const getYoutubePreviews = (text) => parseYoutubeLinksFromText(text)

const messageTextForDisplay = (text) => {
  const previews = getYoutubePreviews(text)
  return textWithoutYoutubeUrls(text, previews)
}

const renderMessageHtml = (text) => {
  return formatWhatsAppMessageHtml(normalizeMessageLineBreaks(text), {
    linkClass: 'task-chat-link'
  })
}

const imageAttachments = (message) =>
  parseAttachments(message?.anexos).filter((a) => isImageAttachment(a))

const fileAttachments = (message) =>
  parseAttachments(message?.anexos).filter((a) => !isImageAttachment(a))

const resolveAttachmentUrl = (attachment) => {
  if (!attachment) return ''
  if (attachment.urlAcesso) return attachment.urlAcesso
  if (attachment.id) {
    const base = isLeadMode.value ? 'anexos-lead' : (isClienteMode.value ? 'anexos-cliente' : 'anexos')
    return `${api.defaults.baseURL}/${base}/${attachment.id}/conteudo`
  }
  return ''
}

const loadMessages = async () => {
  if (endpointUnavailable.value) {
    messages.value = []
    return
  }

  if (!chatMessagesPath.value) {
    messages.value = []
    return
  }
  loading.value = true
  try {
    const response = await api.get(chatMessagesPath.value, {
      params: { _t: Date.now() }
    })
    const raw = Array.isArray(response.data) ? response.data : []
    messages.value = raw.map(enrichMessage)
    await scrollToBottom()
  } catch (error) {
    const status = error?.response?.status
    const code = error?.code
    if (status === 404 || code === 'ERR_NETWORK') {
      endpointUnavailable.value = true
      console.warn('[TaskChat] Endpoint indisponível no backend atual. Chat desativado para compatibilidade.')
    } else {
      console.error('Erro ao carregar mensagens do chat:', error)
    }
    messages.value = []
  } finally {
    loading.value = false
  }
}

const openFileDialog = () => {
  fileInput.value?.click()
}

const openImagePreview = (attachment) => {
  previewImage.value = attachment
}

const closeImagePreview = () => {
  previewImage.value = null
}

let unregisterPreviewEscape = null

watch(previewImage, (val) => {
  if (unregisterPreviewEscape) {
    unregisterPreviewEscape()
    unregisterPreviewEscape = null
  }
  if (val) {
    unregisterPreviewEscape = pushEscapeLayer(closeImagePreview)
  }
})

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve((reader.result || '').toString().split(',')[1] || '')
    reader.onerror = reject
  })
}

const uploadFile = async (file) => {
  if (isLeadMode.value) {
    if (!props.leadId) throw new Error('Lead inválido para upload')
    const base64 = await fileToBase64(file)
    const payload = {
      leadId: props.leadId,
      nomeArquivo: file.name,
      nomeOriginal: file.name,
      tipoArquivo: file.type || 'application/octet-stream',
      tamanho: file.size,
      conteudoBase64: base64
    }
    const response = await api.post('/anexos-lead/upload', payload)
    return response.data
  }

  if (isClienteMode.value) {
    if (!props.clienteId) throw new Error('Cliente inválido para upload')
    const base64 = await fileToBase64(file)
    const payload = {
      clienteId: props.clienteId,
      nomeArquivo: file.name,
      nomeOriginal: file.name,
      tipoArquivo: file.type || 'application/octet-stream',
      tamanho: file.size,
      conteudoBase64: base64
    }
    const response = await api.post('/anexos-cliente/upload', payload)
    return response.data
  }

  if (!props.taskId) throw new Error('Task inválida para upload')

  const base64 = await fileToBase64(file)
  const payload = {
    taskId: props.taskId,
    nomeArquivo: file.name,
    nomeOriginal: file.name,
    tipoArquivo: file.type || 'application/octet-stream',
    tamanho: file.size,
    conteudoBase64: base64
  }

  const response = await api.post('/anexos/upload', payload)
  emit('chatAnexosUpdated')
  return response.data
}

const processAndQueueFiles = async (files) => {
  if (endpointUnavailable.value) return
  if (!files || files.length === 0) return

  uploading.value = true
  try {
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        continue
      }
      const uploaded = await uploadFile(file)
      pendingAttachments.value.push({
        id: uploaded.id,
        nomeOriginal: uploaded.nomeOriginal,
        tipoArquivo: uploaded.tipoArquivo,
        tamanho: uploaded.tamanho,
        urlAcesso: uploaded.urlAcesso,
        ehImagem: uploaded.ehImagem
      })
    }
  } catch (error) {
    console.error('Erro ao anexar arquivo no chat:', error)
  } finally {
    uploading.value = false
  }
}

const handleFileSelect = async (event) => {
  const files = Array.from(event.target?.files || [])
  await processAndQueueFiles(files)
  if (event.target) event.target.value = ''
}

const handlePasteInChat = async (event) => {
  if (endpointUnavailable.value) return
  const items = Array.from(event.clipboardData?.items || [])
  if (items.length === 0) return

  const files = []
  for (const item of items) {
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file) files.push(file)
    }
  }

  if (files.length === 0) return
  event.preventDefault()
  event.stopPropagation()
  await processAndQueueFiles(files)
}

const removePendingAttachment = (attachment) => {
  pendingAttachments.value = pendingAttachments.value.filter(a => a !== attachment)
}

const sendMessage = async () => {
  if (endpointUnavailable.value) return
  if (!chatMessagesPath.value) return
  const mensagem = (newMessage.value || '').trim()
  if (!mensagem && pendingAttachments.value.length === 0) return

  sending.value = true
  try {
    const payload = {
      mensagem,
      anexos: pendingAttachments.value
    }

    const response = await api.post(chatMessagesPath.value, payload)
    const created = response.data

    if (created && !messages.value.some(m => Number(m.id) === Number(created.id))) {
      messages.value.push(enrichMessage(created))
    }

    newMessage.value = ''
    pendingAttachments.value = []
    await scrollToBottom()
  } catch (error) {
    console.error('Erro ao enviar mensagem do chat:', error)
  } finally {
    sending.value = false
  }
}

const onRealtimeTaskMessage = async (event) => {
  const payload = event?.detail
  if (isLeadMode.value) return
  if (!payload || Number(payload.taskId) !== Number(props.taskId)) return

  const exists = messages.value.some(m => Number(m.id) === Number(payload.id))
  if (!exists) {
    messages.value.push(enrichMessage(payload))
    await scrollToBottom()
  }
}

const onRealtimeLeadMessage = async (event) => {
  const payload = event?.detail
  if (!isLeadMode.value) return
  if (!payload || Number(payload.leadId) !== Number(props.leadId)) return

  const exists = messages.value.some(m => Number(m.id) === Number(payload.id))
  if (!exists) {
    messages.value.push(enrichMessage(payload))
    await scrollToBottom()
  }
}

const onRealtimeClienteMessage = async (event) => {
  const payload = event?.detail
  if (!isClienteMode.value) return
  if (!payload || Number(payload.clienteId) !== Number(props.clienteId)) return

  const exists = messages.value.some(m => Number(m.id) === Number(payload.id))
  if (!exists) {
    messages.value.push(enrichMessage(payload))
    await scrollToBottom()
  }
}

const resetAndLoad = async () => {
  if (props.planBlocked) {
    messages.value = []
    loading.value = false
    return
  }
  endpointUnavailable.value = false
  newMessage.value = ''
  pendingAttachments.value = []
  await loadMessages()
}

watch(() => [props.taskId, props.leadId, props.clienteId, props.planBlocked], resetAndLoad, { immediate: true })

onMounted(() => {
  try {
    const rawUser = localStorage.getItem('user')
    if (rawUser) {
      const parsed = JSON.parse(rawUser)
      currentUserId.value = parsed?.id ?? parsed?.usuarioId ?? null
    }
  } catch (e) {
    currentUserId.value = null
  }
  window.addEventListener('task.chat.message', onRealtimeTaskMessage)
  window.addEventListener('lead.chat.message', onRealtimeLeadMessage)
  window.addEventListener('cliente.chat.message', onRealtimeClienteMessage)
})

onBeforeUnmount(() => {
  if (unregisterPreviewEscape) {
    unregisterPreviewEscape()
    unregisterPreviewEscape = null
  }
  window.removeEventListener('task.chat.message', onRealtimeTaskMessage)
  window.removeEventListener('lead.chat.message', onRealtimeLeadMessage)
  window.removeEventListener('cliente.chat.message', onRealtimeClienteMessage)
})
</script>

<style scoped>
.task-chat-bubble--in {
  background: #fff;
  color: #111b21;
}

.task-chat-bubble--out {
  background: #e7f5ff;
  color: #111b21;
}

:global(body.tema_escura) .task-chat-bubble--in,
:global(.dark) .task-chat-bubble--in {
  background: #202c33;
  color: #e9edef;
}

:global(body.tema_escura) .task-chat-bubble--out,
:global(.dark) .task-chat-bubble--out {
  background: #182229;
  color: #111b21;
}

:global(body.tema_escura) .task-chat-bubble--out .task-chat-bubble__text,
:global(.dark) .task-chat-bubble--out .task-chat-bubble__text,
:global(body.tema_escura) .task-chat-bubble--out span.font-medium,
:global(.dark) .task-chat-bubble--out span.font-medium {
  color: #111b21;
}

.whatsapp-message-body {
  white-space: pre-line;
  word-break: break-word;
}

.whatsapp-message-body :deep(.task-chat-link) {
  color: #2563eb;
  text-decoration: underline;
  word-break: break-all;
}

:global(body.tema_escura) .whatsapp-message-body :deep(.task-chat-link),
:global(.dark) .whatsapp-message-body :deep(.task-chat-link) {
  color: #93c5fd;
}

.wa-media__img {
  display: block;
  max-height: 280px;
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.wa-doc-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  min-width: 200px;
  max-width: 100%;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease, transform 0.1s ease;
}

.wa-doc-card:hover {
  transform: translateY(-1px);
}

.wa-doc-card--in {
  background: rgba(0, 0, 0, 0.04);
}

.wa-doc-card--in:hover {
  background: rgba(0, 0, 0, 0.07);
}

.wa-doc-card--out {
  background: rgba(255, 255, 255, 0.45);
}

.wa-doc-card--out:hover {
  background: rgba(255, 255, 255, 0.65);
}

:global(body.tema_escura) .wa-doc-card--in,
:global(.dark) .wa-doc-card--in {
  background: rgba(255, 255, 255, 0.06);
}

:global(body.tema_escura) .wa-doc-card--out,
:global(.dark) .wa-doc-card--out {
  background: rgba(0, 0, 0, 0.2);
}

.wa-doc-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #fff;
}

.wa-doc-icon--pdf { background: linear-gradient(135deg, #e53935, #c62828); }
.wa-doc-icon--sheet { background: linear-gradient(135deg, #43a047, #2e7d32); }
.wa-doc-icon--doc { background: linear-gradient(135deg, #1e88e5, #1565c0); }
.wa-doc-icon--archive { background: linear-gradient(135deg, #fb8c00, #ef6c00); }
.wa-doc-icon--default { background: linear-gradient(135deg, #78909c, #546e7a); }

.wa-doc-card__name {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wa-doc-card__meta {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 2px;
}

.wa-doc-card__action {
  flex-shrink: 0;
  opacity: 0.55;
}

.task-chat-plan-lock {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  color: var(--accent-primary);
  opacity: 0.9;
}

.task-chat-plan-lock svg {
  width: 1rem;
  height: 1rem;
}

.task-chat-plan-gate.plan-feature-gate,
.task-chat-plan-gate:deep(.plan-feature-gate) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.task-chat-plan-gate:deep(.plan-feature-gate-content) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.task-chat-plan-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}
</style>
